import type { Doc, Layouter } from '@doc';
import type { Point, Rect } from '@geom';
import type { Elements, NID } from '@graph';
import type { UndoManager } from '../reactive/UndoManager.svelte';

export interface CommandState {
  doc: Doc;
  selection: Elements;
  entryPoints?: Map<NID, Point>;
  zoom: number;
  pan: Point;
}

export class CommandContext {
  #state: CommandState;
  #updatedState: Partial<CommandState> = {};
  #commitOnUpdate: boolean;
  #needsUndo = false;

  readonly hovered: Elements;
  readonly undoManager: UndoManager;
  readonly layouter: Layouter;
  readonly elemBounds: Rect;
  readonly minZoom: number;
  readonly maxZoom: number;

  #commit: (result?: Partial<CommandState>) => void;

  update(result?: Partial<CommandState>, needsUndo?: boolean) {
    this.#updatedState = { ...this.#updatedState, ...result };
    this.#needsUndo = this.#needsUndo || (needsUndo ?? false);

    if (this.#commitOnUpdate) {
      this.commit();
    }
  }

  get isUpdated(): boolean {
    return Object.keys(this.#updatedState).length > 0;
  }

  commit() {
    if (this.#needsUndo) {
      this.undoManager.saveUndoState();
      this.#needsUndo = false;
    }
    this.#commit(this.#updatedState);
    this.#updatedState = {};
  }

  constructor(args: {
    doc: Doc;
    selection: Elements;
    zoom: number;
    pan: Point;
    hovered: Elements;
    undoManager: UndoManager;
    layouter: Layouter;
    elemBounds: Rect;
    minZoom: number;
    maxZoom: number;
    commit: (result?: Partial<CommandState>) => void;
    commitOnUpdate: boolean;
  }) {
    this.#state = {
      doc: args.doc,
      selection: args.selection,
      zoom: args.zoom,
      pan: args.pan
    };
    this.hovered = args.hovered;
    this.undoManager = args.undoManager;
    this.layouter = args.layouter;
    this.elemBounds = args.elemBounds;
    this.minZoom = args.minZoom;
    this.maxZoom = args.maxZoom;
    this.#commit = args.commit;
    this.#commitOnUpdate = args.commitOnUpdate;
  }

  get doc(): Doc {
    return this.#updatedState.doc ?? this.#state.doc;
  }

  set doc(doc: Doc) {
    if (this.doc.id !== doc.id) {
      this.#updatedState.doc = doc;
    }
  }

  get selection(): Elements {
    return this.#updatedState.selection ?? this.#state.selection;
  }

  set selection(selection: Elements) {
    if (!this.selection.equals(selection)) {
      this.#updatedState.selection = selection;
    }
  }

  get zoom(): number {
    return this.#updatedState.zoom ?? this.#state.zoom;
  }

  set zoom(zoom: number) {
    if (this.zoom !== zoom) {
      this.#updatedState.zoom = zoom;
    }
  }

  get pan(): Point {
    return this.#updatedState.pan ?? this.#state.pan;
  }

  set pan(pan: Point) {
    if (!this.pan.equals(pan)) {
      this.#updatedState.pan = pan;
    }
  }
}
